import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as seguimientosApi from '@/api/seguimientos.api';
import { ApiError } from '@/api/client';
import { HojaDePestana, PestanaSinRegistro } from '@/components/pestanas/HojaDePestana';
import { Button } from '@/components/ui/Button';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { zoomsComoBloques, type BloqueDeZoom } from '@/lib/agenda-zoom';
import { abrirPestana } from '@/store/pestanas.store';
import { ZONA_DEL_NEGOCIO, LUGAR_DE_LA_HORA } from '@/lib/zona';

/** La hora de la cita en la del negocio, que es la que vale aunque el panel se abra desde otro lado. */
const enLaDelNegocio = (iso: string) =>
  new Date(iso).toLocaleString('es', { timeZone: ZONA_DEL_NEGOCIO, weekday: 'long', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit' });

/** La hoja de una cita, sin estado: lo prueba renderToStaticMarkup. */
export function ZoomHojaView({
  zoom,
  ocupado,
  onTuveElZoom,
  onNoSeHizo,
}: {
  zoom: BloqueDeZoom;
  ocupado: boolean;
  onTuveElZoom: () => void;
  onNoSeHizo: () => void;
}) {
  return (
    <div className="space-y-3 text-sm">
      <p className="font-medium text-slate-900 dark:text-slate-100">
        {zoom.conQuien}
        {zoom.telefono && zoom.telefono !== zoom.conQuien ? ` · ${zoom.telefono}` : ''}
      </p>
      <p className="text-slate-600 first-letter:uppercase dark:text-slate-300">{enLaDelNegocio(zoom.fechaCompromiso)} (hora de {LUGAR_DE_LA_HORA})</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={onTuveElZoom} loading={ocupado} disabled={!zoom.conversationId}>
          Tuve el Zoom
        </Button>
        <Button size="sm" variant="outline" onClick={onNoSeHizo} disabled={ocupado}>
          No se hizo
        </Button>
        {zoom.conversationId && (
          <Link to={`/conversations/${zoom.conversationId}`} className="text-sm font-medium text-brand-600 underline-offset-2 hover:underline">
            Abrir el chat
          </Link>
        )}
      </div>
    </div>
  );
}

/**
 * La hoja con sus acciones. «Tuve el Zoom» es la misma ruta que el botón del chat
 * (Ola B): cierra la cita y deja «caerle arriba» a las 24 h. «No se hizo» la saca de
 * la agenda (Saltar).
 */
export function ZoomHoja({ zoom, onCerrar }: { zoom: BloqueDeZoom | null; onCerrar: () => void }) {
  // Desde T12 no es una ventana encima de la agenda: abre una PESTAÑA arriba y no dibuja nada.
  useEffect(() => {
    if (!zoom) return;
    abrirPestana({ tipo: 'zoom', id: zoom.id, titulo: `Zoom con ${zoom.conQuien}`, datos: zoom });
    onCerrar();
  }, [zoom, onCerrar]);
  return null;
}

/**
 * La hoja de un Zoom, como contenido de su pestaña. La cita sale de la misma consulta que la
 * agenda (`['seguimientos', 'zoom']`), así que tras recargar se vuelve a pintar por su id;
 * `datos` (la cita con la que se abrió) solo sirve para pintarla ya, mientras llega la lista.
 */
export function ZoomEnPestana({ id, datos, onCerrar }: { id: string; datos?: unknown; onCerrar: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const zooms = useQuery({ queryKey: ['seguimientos', 'zoom'], queryFn: () => seguimientosApi.listar('zoom') });
  const zoom = zooms.data ? zoomsComoBloques(zooms.data).find((z) => z.id === id) : (datos as BloqueDeZoom | undefined);
  const listo = (mensaje: string) => {
    void qc.invalidateQueries({ queryKey: ['seguimientos'] });
    toast.success(mensaje);
    onCerrar();
  };
  const fallo = (err: unknown) => toast.error(err instanceof ApiError ? err.message : 'No se pudo guardar');
  const tuve = useMutation({
    mutationFn: () => seguimientosApi.etapa(zoom!.conversationId!, 'zoom_hecho'),
    onSuccess: () => listo('Anotado: tuviste el Zoom. Mañana te toca caerle arriba.'),
    onError: fallo,
  });
  const noSeHizo = useMutation({
    mutationFn: () => seguimientosApi.accion(zoom!.id, 'saltar'),
    onSuccess: () => listo('La cita salió de la agenda'),
    onError: fallo,
  });
  if (!zoom) {
    if (zooms.isPending) return <SpinnerOverlay label="Abriendo el Zoom…" />;
    return (
      <PestanaSinRegistro
        nombre="Zoom"
        mensaje={zooms.isError ? 'No se pudo abrir esta cita. Revisa tu conexión y vuelve a abrirla.' : 'Esta cita ya no está en la agenda: se hizo, se movió o se canceló.'}
        onCerrar={onCerrar}
      />
    );
  }
  return (
    <HojaDePestana nombre="Zoom" ancho="max-w-md">
      <div className="min-h-0 flex-1 overflow-y-auto p-5">
        <h2 className="mb-3 text-lg font-semibold text-slate-900 dark:text-slate-100">Zoom</h2>
        <ZoomHojaView
          zoom={zoom}
          ocupado={tuve.isPending || noSeHizo.isPending}
          onTuveElZoom={() => tuve.mutate()}
          onNoSeHizo={() => noSeHizo.mutate()}
        />
      </div>
    </HojaDePestana>
  );
}
